import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Volume2, VolumeX, ChevronLeft, ChevronRight, Play, Pause, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isNative = (url) => !!url && (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url) || url.startsWith('/api/files/'));
const resolveUrl = (url) => (url && url.startsWith('/api/')) ? `${BACKEND_URL}${url}` : url;

/**
 * FeaturedReel — vertical 9:16 highlight reel that auto-cycles between videos.
 * Channel-driven: 'dreams_league' shows sports highlights, 'flyt' shows agency reel.
 * Admin can swap content from /admin → Featured Reel tab.
 */
const FeaturedReel = ({ channel = 'dreams_league', accentClass = 'text-blue-500', isDark = false }) => {
  const [videos, setVideos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/featured-reel`, { params: { channel } })
      .then((r) => setVideos(r.data || []))
      .catch(() => setVideos([]))
      .finally(() => setLoaded(true));
  }, [channel]);

  // Filter to only playable native videos (autoplay only works with native <video>).
  const playable = videos.filter((v) => isNative(v.video_url));
  const current = playable[idx % Math.max(playable.length, 1)];

  const advance = useCallback(() => {
    setIdx((i) => (i + 1) % Math.max(playable.length, 1));
  }, [playable.length]);

  const back = useCallback(() => {
    setIdx((i) => (i - 1 + playable.length) % Math.max(playable.length, 1));
  }, [playable.length]);

  // When video changes, reset the player.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    v.currentTime = 0;
    if (!paused) v.play().catch(() => {});
  }, [idx, muted, paused, current?.video_url]);

  if (!loaded) return null;
  if (playable.length === 0) return null; // silently no-op if nothing's been seeded

  const heading = channel === 'flyt' ? 'The Reel' : 'On The Field';
  const eyebrow = channel === 'flyt' ? 'FLYT Studios · Highlights' : 'Dreams League · Highlights';
  const tagline = channel === 'flyt'
    ? 'A rolling tape of the work — short-form, long-form, brand and creator drops.'
    : "The plays, the moments, the people. Auto-rolling highlights from across the league.";

  return (
    <section
      data-testid="featured-reel"
      data-channel={channel}
      className={`relative overflow-hidden ${isDark ? 'bg-black' : 'bg-[#0a0a0a]'}`}
    >
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            channel === 'flyt'
              ? 'radial-gradient(circle at 15% 30%, rgba(212,175,55,0.4), transparent 55%), radial-gradient(circle at 85% 70%, rgba(240,216,117,0.3), transparent 55%)'
              : 'radial-gradient(circle at 20% 20%, rgba(59,130,246,0.45), transparent 55%), radial-gradient(circle at 80% 80%, rgba(96,165,250,0.35), transparent 55%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* LEFT — heading + control deck */}
        <div className="text-white order-2 lg:order-1">
          <p className={`font-italic text-sm tracking-wider uppercase mb-3 ${channel === 'flyt' ? 'text-yellow-300' : 'text-blue-400'}`}>
            {eyebrow}
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95] mb-5">
            {heading}
          </h2>
          <p className="font-body text-base lg:text-lg text-gray-300 leading-relaxed mb-8 max-w-md">
            {tagline}
          </p>

          {/* Now-playing card */}
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 lg:p-6 mb-6" data-testid="reel-now-playing">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className={`w-4 h-4 ${channel === 'flyt' ? 'text-yellow-300' : 'text-blue-400'}`} />
              <p className="font-display text-xs uppercase tracking-widest text-gray-400">Now Playing</p>
            </div>
            <p className="font-display text-xl lg:text-2xl uppercase leading-tight" data-testid="reel-title">
              {current.title}
            </p>
            {current.subtitle && (
              <p className="font-italic text-gray-400 text-sm mt-1" data-testid="reel-subtitle">
                {current.subtitle}
              </p>
            )}
            <div className="flex items-center gap-2 mt-4">
              {playable.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Jump to clip ${i + 1}`}
                  data-testid={`reel-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx % playable.length
                      ? `${channel === 'flyt' ? 'bg-yellow-300' : 'bg-blue-400'} w-8`
                      : 'bg-white/20 hover:bg-white/40 w-4'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={back}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition"
              aria-label="Previous clip"
              data-testid="reel-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition"
              aria-label={paused ? 'Play' : 'Pause'}
              data-testid="reel-play-pause"
            >
              {paused ? <Play className="w-5 h-5 fill-white ml-0.5" /> : <Pause className="w-5 h-5 fill-white" />}
            </button>
            <button
              type="button"
              onClick={advance}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition"
              aria-label="Next clip"
              data-testid="reel-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? 'Unmute video' : 'Mute video'}
              aria-pressed={!muted}
              className={`ml-2 inline-flex items-center gap-2 px-4 h-11 rounded-full border border-white/20 transition font-bold text-sm ${
                muted ? 'bg-white text-black hover:bg-gray-200' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              data-testid="reel-mute"
            >
              {muted ? <><VolumeX className="w-4 h-4" /> Unmute</> : <><Volume2 className="w-4 h-4" /> Mute</>}
            </button>
          </div>
        </div>

        {/* RIGHT — vertical 9:16 player */}
        <div className="order-1 lg:order-2 flex justify-center">
          <div
            className="relative w-full max-w-[360px] aspect-[9/16] rounded-2xl overflow-hidden bg-black ring-1 ring-white/10 shadow-2xl"
            data-testid="reel-player"
          >
            <video
              key={current.id}
              ref={videoRef}
              src={resolveUrl(current.video_url)}
              poster={current.poster ? resolveUrl(current.poster) : undefined}
              autoPlay
              playsInline
              muted={muted}
              onEnded={advance}
              onClick={() => setMuted((m) => !m)}
              className="w-full h-full object-cover cursor-pointer"
              data-testid="reel-video-el"
            />
            {/* Subtle gradient overlay for legibility */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="pointer-events-none absolute bottom-3 left-3 right-3 text-white">
              <p className="font-display text-xs uppercase tracking-widest opacity-80">{current.subtitle || (channel === 'flyt' ? 'FLYT Studios' : 'Dreams League')}</p>
              <p className="font-display text-base uppercase leading-tight line-clamp-1">{current.title}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedReel;
